import { ApiError, useGameLoginToken } from '@ottd/api';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useAuth } from '@/auth/use-auth';
import { BevelWindow } from '@/components/prototype/bevel-window';
import { QueryState } from '@/components/prototype/query-state';
import { Button } from '@/components/ui/button';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';
import { useResendVerification } from '@/pages/auth/use-resend-verification';

const MS_PER_SECOND = 1000;

/**
 * Seconds until the next TOTP window: windows are aligned to multiples of
 * the period since the Unix epoch, so no server timestamp is needed.
 */
function secondsUntilRefresh(periodSeconds: number): number {
  const nowSeconds = Math.floor(Date.now() / MS_PER_SECOND);
  return periodSeconds - (nowSeconds % periodSeconds);
}

function useRefreshCountdown(periodSeconds: number | undefined): number | null {
  const [remaining, setRemaining] = useState<number | null>(null);

  useEffect(() => {
    if (!periodSeconds) {
      setRemaining(null);
      return;
    }
    setRemaining(secondsUntilRefresh(periodSeconds));
    const timer = window.setInterval(
      () => setRemaining(secondsUntilRefresh(periodSeconds)),
      MS_PER_SECOND,
    );
    return () => window.clearInterval(timer);
  }, [periodSeconds]);

  return remaining;
}

/** The token endpoint sits behind `verified`; an unverified account gets 403. */
function isUnverifiedError(error: unknown, emailVerified: boolean): boolean {
  return !emailVerified && error instanceof ApiError && error.status === 403;
}

/** Shown instead of the generic 403 label: what to do to get a token. */
function VerifyEmailNotice() {
  const { t } = useTranslation('auth');
  const { sending, resend } = useResendVerification();

  return (
    <div className="flex flex-col items-start gap-3">
      <p className="font-mono text-sm text-dim">{t('settings.gameLoginUnverified')}</p>
      <Button type="button" variant="outline" size="sm" disabled={sending} onClick={resend}>
        {t('verifyEmail.resend')}
      </Button>
    </div>
  );
}

/**
 * The rotating in-game `!login` token with copy button, refresh countdown and
 * the authenticator QR. Used by the login token page and the settings screen.
 */
export function GameLoginTokenSection() {
  const { t } = useTranslation('auth');
  const { user } = useAuth();
  const { data, isLoading, isError, error } = useGameLoginToken();
  const { copied, copy } = useCopyToClipboard();
  const remaining = useRefreshCountdown(data?.refreshSeconds);

  const handleCopy = async (command: string) => {
    if (!(await copy(command))) toast.error(t('errors.generic'));
  };

  const emailVerified = user?.email_verified_at !== null && user?.email_verified_at !== undefined;

  return (
    <BevelWindow title={t('settings.gameLogin')}>
      {isUnverifiedError(error, emailVerified) ? (
        <VerifyEmailNotice />
      ) : (
        <QueryState
          isLoading={isLoading}
          isError={isError}
          error={error}
          isEmpty={!data?.loginCommand}
          emptyLabel="No in-game token is available for your account yet."
        >
          {data?.loginCommand ? (
            <div className="flex flex-col gap-3">
              <p className="font-mono text-xs text-dim">
                {t('settings.gameLoginHint', { seconds: data.refreshSeconds })}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <code className="bevel-in bg-code-bg text-code-tx px-3 py-1.5 font-mono text-sm">
                  {data.loginCommand}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopy(data.loginCommand!)}
                >
                  {copied ? t('settings.gameLoginCopied') : t('settings.gameLoginCopy')}
                </Button>
              </div>
              {remaining !== null ? (
                <p className="font-mono text-xs text-dim" aria-live="polite">
                  {t('settings.gameLoginCountdown', { seconds: remaining })}
                </p>
              ) : null}
              {data.provisioningUri ? (
                <div className="flex flex-col gap-2">
                  <span className="text-faint text-[10px] uppercase tracking-wide">
                    {t('settings.gameLoginScan')}
                  </span>
                  <div className="bevel bg-white w-fit p-2">
                    <QRCodeSVG value={data.provisioningUri} size={120} />
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </QueryState>
      )}
    </BevelWindow>
  );
}
